import { ErrorMonitoringService } from "../ErrorMonitoringService";

type SubscriberFn<T = unknown> = (payload: T) => void;
type UnsubscribeFn = () => void;

/**
 * AiAgentThreadPubSubBus is a singleton service that allows components to communicate
 * through named topics without direct dependencies. Publishers can publish
 * messages to topics, and subscribers can listen to those topics.
 *
 * This service operates outside of React's component tree, making it useful
 * for communication between components that don't share a common context.
 */
export class AiAgentThreadPubSubBus {
  private static instance: AiAgentThreadPubSubBus | null = null;
  private subscribers: Map<string, Set<SubscriberFn>> = new Map();

  private constructor() {
    // Private to enforce singleton
  }

  public static getInstance(): AiAgentThreadPubSubBus {
    if (AiAgentThreadPubSubBus.instance === null) {
      AiAgentThreadPubSubBus.instance = new AiAgentThreadPubSubBus();
    }
    return AiAgentThreadPubSubBus.instance;
  }

  public subscribe<T = unknown>(
    topic: string,
    subscriber: SubscriberFn<T>,
  ): UnsubscribeFn {
    if (!this.subscribers.has(topic)) {
      this.subscribers.set(topic, new Set());
    }

    const topicSubscribers = this.subscribers.get(topic)!;
    topicSubscribers.add(subscriber as SubscriberFn);

    return () => {
      topicSubscribers.delete(subscriber as SubscriberFn);
      // Clean up empty topic sets
      if (topicSubscribers.size === 0) {
        this.subscribers.delete(topic);
      }
    };
  }

  public publish<T = unknown>(topic: string, payload: T): boolean {
    const topicSubscribers = this.subscribers.get(topic);

    if (!topicSubscribers || topicSubscribers.size === 0) {
      return false;
    }

    let successCount = 0;
    topicSubscribers.forEach((subscriber) => {
      try {
        subscriber(payload);
        successCount++;
      } catch (error) {
        ErrorMonitoringService.captureError(
          error instanceof Error
            ? error
            : new Error(`Error in subscriber for topic "${topic}"`),
          {
            context: {
              topic,
              payload,
              subscriber,
            },
          },
        );
      }
    });

    return successCount > 0;
  }
}

// Initialize the singleton instance
AiAgentThreadPubSubBus.getInstance();
